import { useEffect, useState, useCallback } from "react";
import { Handshake, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/api";
import { DataTable } from "@/components/common/DataTable";
import { PageHeader, TenantPicker, useTenantScope, Badge, fmtDate, CONSENT_PURPOSES, LEGAL_BASES } from "./shared";

export default function ConsentLedgerPage() {
  const scope = useTenantScope();
  const [data, setData] = useState({ items: [], total: 0 });
  const [filters, setFilters] = useState({ customerId: "", purpose: "", action: "" });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ customerId: "", purpose: "email", action: "granted", legalBasis: "consent" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { ...scope.params, size: 100 };
    if (filters.customerId) params.customerId = filters.customerId;
    if (filters.purpose) params.purpose = filters.purpose;
    if (filters.action) params.action = filters.action;
    try { setData((await api.get("/v1/compliance/consents", { params })).data.data); }
    catch { setData({ items: [], total: 0 }); }
    finally { setLoading(false); }
  }, [scope.params, filters]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.customerId.trim()) { toast.error("Customer ID is required"); return; }
    setSaving(true);
    try {
      await api.post("/v1/compliance/consents", { ...form, ...scope.params });
      toast.success("Consent recorded · preferences synced");
      setShowForm(false);
      setForm({ customerId: "", purpose: "email", action: "granted", legalBasis: "consent" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="consent-ledger-page">
      <PageHeader icon={Handshake} title="Consent Ledger"
        subtitle={`Append-only consent trail · ${data.total} events`}>
        <TenantPicker {...scope} />
        <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 py-1.5 transition-colors" data-testid="record-consent-btn">
          <Plus className="h-4 w-4" /> Record consent
        </button>
      </PageHeader>

      <div className="flex flex-wrap gap-3 mb-4">
        <input placeholder="Customer ID" value={filters.customerId}
          onChange={(e) => setFilters({ ...filters, customerId: e.target.value })}
          className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground w-64" data-testid="filter-customer" />
        <select value={filters.purpose} onChange={(e) => setFilters({ ...filters, purpose: e.target.value })}
          className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground" data-testid="filter-purpose">
          <option value="">All purposes</option>
          {CONSENT_PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filters.action} onChange={(e) => setFilters({ ...filters, action: e.target.value })}
          className="rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground" data-testid="filter-action">
          <option value="">All actions</option>
          <option value="granted">Granted</option>
          <option value="withdrawn">Withdrawn</option>
        </select>
      </div>

      <DataTable testid="consents-table" rows={data.items} loading={loading}
        columns={[
          { key: "createdOn", header: "Time", render: (c) => fmtDate(c.createdOn) },
          { key: "customerId", header: "Customer", render: (c) => <span className="font-mono text-xs">{(c.customerId || "").slice(0, 10)}</span> },
          { key: "purpose", header: "Purpose", render: (c) => <span className="capitalize">{c.purpose?.replace("_", " ")}</span> },
          { key: "action", header: "Action", render: (c) => <Badge label={c.action} kind="purpose" /> },
          { key: "legalBasis", header: "Legal basis", render: (c) => <span className="text-xs text-muted-foreground capitalize">{(c.legalBasis || "").replace("_", " ")}</span> },
          { key: "source", header: "Source", render: (c) => <span className="text-xs">{c.source}</span> },
        ]} />

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="record-consent-modal">
          <div className="bg-card rounded-xl border border-border w-full max-w-md p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground">Record consent event</h3>
              <button onClick={() => setShowForm(false)} data-testid="close-consent-modal"><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Customer ID" value={form.customerId}
                onChange={(e) => setForm({ ...form, customerId: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="consent-customer-input" />
              <select value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="consent-purpose-select">
                {CONSENT_PURPOSES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="consent-action-select">
                <option value="granted">Granted</option>
                <option value="withdrawn">Withdrawn</option>
              </select>
              <select value={form.legalBasis} onChange={(e) => setForm({ ...form, legalBasis: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="consent-basis-select">
                {LEGAL_BASES.map((b) => <option key={b} value={b}>{b.replace("_", " ")}</option>)}
              </select>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowForm(false)} className="rounded-lg border border-border px-3 py-1.5 text-sm">Cancel</button>
              <button onClick={submit} disabled={saving} className="rounded-lg bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 text-sm disabled:opacity-50" data-testid="submit-consent-btn">
                {saving ? "Saving…" : "Record"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
